<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxLookup,
  DxScrolling,
  DxSorting,
  type DxDataGridTypes,
} from 'devextreme-vue/data-grid';
import {
  bottoGridPaging,
  getAggregatorsLookup,
  getSenderNamesLookup,
  getTemplatesLookup,
} from '@botto/core';
import {
  smsClient,
  createChildRoutingChoicesStore,
  createGroupMessageTemplatesStore,
  createGroupSenderNamesStore,
  createGroupDomainsStore,
} from '../../api';

const { paging, pager } = bottoGridPaging({ pageSize: 10 });

interface DomainFull {
  id: number;
  domain: string;
  aggregator_id: number;
}

const props = defineProps<{
  groupId: number;
  routingChoiceId: number;
  aggregatorId?: number | null;
}>();

const aggregatorsLookup = getAggregatorsLookup();
const senderNamesLookup = getSenderNamesLookup();
const templatesLookup = getTemplatesLookup();

const childChoicesStore = computed(() =>
  createChildRoutingChoicesStore(props.groupId, props.routingChoiceId),
);
const senderNamesStore = computed(() => createGroupSenderNamesStore(props.routingChoiceId));
const messageTemplatesStore = computed(() =>
  createGroupMessageTemplatesStore(props.routingChoiceId),
);
const domainsStore = computed(() => createGroupDomainsStore(props.routingChoiceId));

const choiceAggregatorId = ref<number | null>(props.aggregatorId ?? null);
/** Domains for lookup — loaded via GET /domains/?aggregator_id=… */
const domainsLookup = ref<DomainFull[]>([]);

const stateItems = ['ACTIVE', 'INACTIVE'];

const filteredSenderNames = computed(() => {
  const aggregatorId = choiceAggregatorId.value;
  const items = senderNamesLookup.items.value;
  if (aggregatorId == null) return items;
  return items.filter((item) => item.aggregator_id === aggregatorId);
});

const filteredTemplates = computed(() => {
  const aggregatorId = choiceAggregatorId.value;
  const items = templatesLookup.items.value;
  if (aggregatorId == null) return items;
  return items.filter((item) => item.aggregator_id === aggregatorId);
});

function aggregatorDisplay(item: { id: number }) {
  return aggregatorsLookup.labelOf(item.id);
}

function senderDisplay(item: { id: number }) {
  return senderNamesLookup.labelOf(item.id);
}

function templateDisplay(item: { id: number }) {
  return templatesLookup.labelOf(item.id);
}

function domainDisplay(item: DomainFull) {
  return item.domain;
}

function onInitNewChildRow(e: DxDataGridTypes.InitNewRowEvent) {
  e.data.group_id = props.groupId;
  e.data.parent_id = props.routingChoiceId;
  e.data.state = 'ACTIVE';
  e.data.percentage = 100;
}

function onInitNewSenderRow(e: DxDataGridTypes.InitNewRowEvent) {
  e.data.routing_choice_id = props.routingChoiceId;
  e.data.percentage = 100;
}

function onInitNewTemplateRow(e: DxDataGridTypes.InitNewRowEvent) {
  e.data.routing_choice_id = props.routingChoiceId;
}

function onInitNewDomainRow(e: DxDataGridTypes.InitNewRowEvent) {
  e.data.routing_choice_id = props.routingChoiceId;
  e.data.percentage = 100;
}

watch(
  () => props.aggregatorId,
  (id) => {
    if (id != null) choiceAggregatorId.value = id;
  },
);

async function loadDomainsForAggregator(aggregatorId: number | null) {
  if (aggregatorId == null) {
    domainsLookup.value = [];
    return;
  }
  const { data, error } = await smsClient.GET('/domains/', {
    params: {
      query: {
        aggregator_id: aggregatorId,
        offset: 0,
        limit: 10000,
      },
    },
  });
  if (error) throw error;
  domainsLookup.value = (data?.items ?? []).map((d) => ({
    id: Number(d.id),
    domain: d.domain ?? `#${d.id}`,
    aggregator_id: d.aggregator_id ?? aggregatorId,
  }));
}

watch(
  choiceAggregatorId,
  (id) => {
    void loadDomainsForAggregator(id);
  },
  { immediate: true },
);

onMounted(() => {
  void aggregatorsLookup.ensureLoaded();
  void senderNamesLookup.ensureLoaded();
  void templatesLookup.ensureLoaded();
});
</script>

<template>
  <div class="routing-choice-detail">
    <DxTabPanel :animation-enabled="true" :swipe-enabled="false" :defer-rendering="false">
      <DxTabItem title="Запасные агрегаторы">
        <DxDataGrid
          :data-source="childChoicesStore"
          :show-borders="true"
          :column-auto-width="true"
          :repaint-changes-only="true"
          key-expr="id"
          :paging="paging"
          :pager="pager"
          @init-new-row="onInitNewChildRow"
        >
          <DxEditing
            :allow-adding="true"
            :allow-updating="true"
            :allow-deleting="true"
            mode="row"
            :use-icons="true"
          />
          <DxColumn data-field="id" caption="ID" :allow-editing="false" width="70" />
          <DxColumn data-field="aggregator_id" caption="Агрегатор">
            <DxLookup
              :data-source="aggregatorsLookup.items.value"
              value-expr="id"
              :display-expr="aggregatorDisplay"
            />
          </DxColumn>
          <DxColumn data-field="state" caption="Статус">
            <DxLookup :data-source="stateItems" />
          </DxColumn>
          <DxColumn
            data-field="percentage"
            caption="%"
            data-type="number"
            :editor-options="{ min: 0, max: 100 }"
            width="90"
          />
          <DxScrolling mode="standard" />
          <DxSorting mode="single" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="Имена отправителей">
        <DxDataGrid
          :data-source="senderNamesStore"
          :show-borders="true"
          :column-auto-width="true"
          :repaint-changes-only="true"
          key-expr="id"
          :paging="paging"
          :pager="pager"
          @init-new-row="onInitNewSenderRow"
        >
          <DxEditing
            :allow-adding="true"
            :allow-updating="true"
            :allow-deleting="true"
            mode="row"
            :use-icons="true"
          />
          <DxColumn data-field="id" caption="ID" :allow-editing="false" width="70" />
          <DxColumn data-field="sender_name_id" caption="Имя отправителя">
            <DxLookup
              :data-source="filteredSenderNames"
              value-expr="id"
              :display-expr="senderDisplay"
            />
          </DxColumn>
          <DxColumn
            data-field="percentage"
            caption="%"
            data-type="number"
            :editor-options="{ min: 0, max: 100 }"
            width="90"
          />
          <DxScrolling mode="standard" />
          <DxSorting mode="single" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="Шаблоны сообщений">
        <DxDataGrid
          :data-source="messageTemplatesStore"
          :show-borders="true"
          :column-auto-width="true"
          :repaint-changes-only="true"
          key-expr="id"
          :paging="paging"
          :pager="pager"
          @init-new-row="onInitNewTemplateRow"
        >
          <DxEditing
            :allow-adding="true"
            :allow-updating="true"
            :allow-deleting="true"
            mode="row"
            :use-icons="true"
          />
          <DxColumn data-field="id" caption="ID" :allow-editing="false" width="70" />
          <DxColumn data-field="message_template_id" caption="Шаблон">
            <DxLookup
              :data-source="filteredTemplates"
              value-expr="id"
              :display-expr="templateDisplay"
            />
          </DxColumn>
          <DxScrolling mode="standard" />
          <DxSorting mode="single" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="Домены">
        <DxDataGrid
          :data-source="domainsStore"
          :show-borders="true"
          :column-auto-width="true"
          :repaint-changes-only="true"
          key-expr="id"
          :paging="paging"
          :pager="pager"
          @init-new-row="onInitNewDomainRow"
        >
          <DxEditing
            :allow-adding="true"
            :allow-updating="true"
            :allow-deleting="true"
            mode="row"
            :use-icons="true"
          />
          <DxColumn data-field="id" caption="ID" :allow-editing="false" width="70" />
          <DxColumn data-field="domain_id" caption="Домен">
            <DxLookup
              :data-source="domainsLookup"
              value-expr="id"
              :display-expr="domainDisplay"
            />
          </DxColumn>
          <DxColumn
            data-field="percentage"
            caption="%"
            data-type="number"
            :editor-options="{ min: 0, max: 100 }"
            width="90"
          />
          <DxScrolling mode="standard" />
          <DxSorting mode="single" />
        </DxDataGrid>
      </DxTabItem>
    </DxTabPanel>
  </div>
</template>

<style scoped>
.routing-choice-detail {
  padding: 20px;
  background: var(--dx-color-main-bg, #fff);
}
</style>
